import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import { compose } from "redux";
import styled, { withTheme, useTheme } from "styled-components";
import { Seq, Record } from "immutable";

import { MINIMUM_HEIGHT_TO_SHOW_MOBILE_BOOKING_SUMMARY } from "@config/consts";
import {
  END_OF_TENANCY_SUMMARY,
  END_OF_TENANCY_SUMMARY_EXTRAS,
} from "@config/testIds/endOfTenancy";
import { BOOKING_SUMMARY } from "@config/testIds/booking";
import { getPriceSubpartTestId } from "@config/testIds/priceSubparts";

import { TYPE_OF_SUBPARTS } from "@typings/priceSubparts";
import { AVAILABLE_COUNTRIES } from "@typings/globals";
import { LOADING_STATE } from "@typings/reduxThunkActions";

import { TestWrapper } from "@ui/Atoms/TestWrapper";
import { MobileBookingSummary } from "@ui/Organisms/MobileBookingSummary";
import {
  Box,
  BoxDivider,
  SidebarSummaryTotal,
  PriceSubparts,
} from "@ui/Molecules";
import { ITheme } from "@ui/themes/ThemeInterface";
import {
  BaggageBags,
  ClockTime,
  CalendarScheduleLines,
  ShieldChekmark,
} from "@ui/Assets/MyIcons";
import { text } from "@ui/helpers";

import { IntlDateFormat, intlFormatDate } from "@services/IntlFormatDate";
import { kebabToSnake } from "@services/KebabToSnake";

import { useTranslate } from "@hooks/useTranslate";
import { useResize } from "@hooks/useResize";
import { useScrollPosition, SCROLL_DIR } from "@hooks/useScrollPosition";
import { useActiveElement } from "@hooks/useActiveElement";
import { useBreakpoint } from "@hooks/useBreakpoint";
import { useLocalisation } from "@hooks/useLocalisation";
import { useVoucherManager } from "@hooks/useVoucherManager";

import {
  getPricing,
  getSkipAgentAssignment,
  getTasks,
  getWarranty,
  getShowOffer,
  getExecutionDateAsDateObject,
  getPricingLoadingState,
} from "@app/redux/endOfTenancy/endOfTenancy.selectors";
import { resetVoucher } from "@app/redux/endOfTenancy/endOfTenancy.actions";
import {
  END_OF_TENANCY_EXTRA_SERVICES,
  Task,
} from "@app/redux/endOfTenancy/endOfTenancy.typings";
import { pricingInitialState } from "@app/redux/endOfTenancy/endOfTenancy.factories";

import { EndOfTenancyProcessHelper } from "../EndOfTenancyProcess/EndOfTenancyProcess.helper";

import { EndOfTenancySummaryList } from "./Sidebar.helpers";

interface EndOfTenancySummaryProps {
  theme: ITheme;
  withHeader: boolean;
  isDesktop: boolean;
}

const EndOfTenancySummaryComponent = (
  props: EndOfTenancySummaryProps,
): React.ReactElement | null => {
  const dispatch = useDispatch();
  const translate = useTranslate("batmaid_pwa_end_of_tenancy");
  const translateGeneric = useTranslate("batmaid_pwa_generic");
  const { windowHeight } = useResize();
  const { country } = useLocalisation();
  const theme = useTheme();

  const executionDateObj = useSelector(getExecutionDateAsDateObject);
  const showOffer = useSelector(getShowOffer);
  const pricing = useSelector(getPricing);
  const pricingState = useSelector(getPricingLoadingState);
  const warranty = useSelector(getWarranty);
  const tasks: Seq.Keyed<
    END_OF_TENANCY_EXTRA_SERVICES,
    Record<Task>
  > = useSelector(getTasks);
  const skipAgentAssignment = useSelector(getSkipAgentAssignment);

  const { isFocusedOnTextInputOrTextarea } = useActiveElement();
  const { desktop, desktopLarge } = useBreakpoint("v1");

  const isSubtotalSectionVisible = React.useMemo(
    () => !!pricing.get("discountsToDisplay").size,
    [!!pricing.get("discountsToDisplay").size],
  );

  const isDesktop = desktop || desktopLarge;

  const isEndOfTenancySummaryVisible =
    isDesktop ||
    (!isDesktop &&
      windowHeight > MINIMUM_HEIGHT_TO_SHOW_MOBILE_BOOKING_SUMMARY &&
      !isFocusedOnTextInputOrTextarea);

  const { scrollDir } = useScrollPosition();
  const voucherManager = useVoucherManager();

  const handleVoucherReset = () => {
    voucherManager.removeStoredDeeplinkVoucher();
    voucherManager.removeStoredVoucher();
    dispatch(resetVoucher());
  };

  if (!isEndOfTenancySummaryVisible) {
    return null;
  }

  if (!isDesktop) {
    return (
      <MobileBookingSummary.Summary
        testId={BOOKING_SUMMARY.WRAPPER}
        testIds={{
          price: END_OF_TENANCY_SUMMARY.PRICE,
        }}
        price={
          !showOffer
            ? pricingInitialState.displayedPriceForFirstBooking
            : pricing.get("displayedPriceForFirstBooking")
        }
        currency={pricing.get("currency")}
        tooltip={translate("price_tooltip")}
        translations={{
          totalText: translateGeneric("total"),
        }}
        isHighlighted={country === AVAILABLE_COUNTRIES.FR}
        isLoading={pricingState === LOADING_STATE.LOADING}
      >
        <React.Fragment>
          <MobileBookingSummary.ChildWrapper>
            <MobileBookingSummary.List>
              {/* Cleaning type */}
              <MobileBookingSummary.ListItem>
                <div>
                  <BaggageBags width={theme.icons.sizes.base_x3} />
                </div>

                <div>
                  {translate("end_of_tenancy_cleaning")}

                  {/* Extras */}
                  {showOffer &&
                    tasks
                      .valueSeq()
                      .filter(
                        (task: Record<Task>) =>
                          task.get("service") !==
                          END_OF_TENANCY_EXTRA_SERVICES.END_OF_TENANCY_CLEANING,
                      )
                      .filter((task: Record<Task>) => task.get("selected"))
                      .map((task: Record<Task>, index) => {
                        const testIdKey = kebabToSnake(
                          task.get("service"),
                        ) as keyof typeof END_OF_TENANCY_SUMMARY_EXTRAS;
                        return (
                          <TestWrapper
                            testId={END_OF_TENANCY_SUMMARY_EXTRAS[testIdKey]}
                            key={index}
                          >
                            <div>
                              +{" "}
                              {translate(
                                EndOfTenancyProcessHelper.getEndOfTenancyExtraServiceTranslation(
                                  task.get("service"),
                                ),
                              )}
                            </div>
                          </TestWrapper>
                        );
                      })}
                </div>
              </MobileBookingSummary.ListItem>

              {/* Duration */}
              {showOffer && (
                <MobileBookingSummary.ListItem
                  testId={BOOKING_SUMMARY.DURATION}
                >
                  <div>
                    <ClockTime width={theme.icons.sizes.base_x3} />
                  </div>

                  <div>{`${
                    pricing.get("recommendedBasisHours") +
                    pricing.get("recommendedExtraHours")
                  }
          ${translate("sidebar_hours_of_home_cleaning")}`}</div>
                </MobileBookingSummary.ListItem>
              )}

              {/* Warranty */}
              {warranty && (
                <MobileBookingSummary.ListItem>
                  <div>
                    <ShieldChekmark width={theme.icons.sizes.base_x3} />
                  </div>

                  <div>{translate("sidebar_with_warranty")}</div>
                </MobileBookingSummary.ListItem>
              )}

              {/* Execution Date */}
              <MobileBookingSummary.ListItem
                testId={BOOKING_SUMMARY.DATE_AND_TIME}
              >
                <div>
                  <CalendarScheduleLines width={theme.icons.sizes.base_x3} />
                </div>

                <div>
                  {intlFormatDate(
                    executionDateObj,
                    IntlDateFormat.weekdayLongFullDate,
                  )}
                </div>
              </MobileBookingSummary.ListItem>

              {/* Skip agent assignment */}
              {skipAgentAssignment && (
                <MobileBookingSummary.ListItem>
                  <div>
                    <ClockTime width={theme.icons.sizes.base_x3} />
                  </div>

                  <div>Skip agent assignment</div>
                </MobileBookingSummary.ListItem>
              )}
            </MobileBookingSummary.List>
          </MobileBookingSummary.ChildWrapper>

          {isSubtotalSectionVisible && (
            <MobileBookingSummary.ChildWrapper>
              <PriceSubparts.List>
                <React.Fragment>
                  {pricing.get("discountsToDisplay").map((subpart, index) => (
                    <PriceSubparts.Item
                      key={`subpart-${subpart.type.toLocaleLowerCase()}-${index}`}
                      testId={getPriceSubpartTestId(subpart.type)}
                      variant={
                        subpart.type === TYPE_OF_SUBPARTS.VOUCHER
                          ? "voucher"
                          : "default"
                      }
                      price={subpart.price}
                      currency={subpart.currency}
                      description={subpart.description}
                      tooltip={subpart.tooltip}
                      onVoucherRemove={handleVoucherReset}
                    >
                      {subpart.label}
                    </PriceSubparts.Item>
                  ))}
                </React.Fragment>
              </PriceSubparts.List>
            </MobileBookingSummary.ChildWrapper>
          )}
        </React.Fragment>
      </MobileBookingSummary.Summary>
    );
  }

  return (
    <StyledBox
      variant="smallPadding"
      isMoved={scrollDir === SCROLL_DIR.DEFAULT || scrollDir === SCROLL_DIR.UP}
      header={props.withHeader ? translate("sidebar_summary") : ""}
    >
      <EndOfTenancySummaryList>
        {/* Cleaning type */}
        <li>
          <BaggageBags width={theme.icons.sizes.base_x3} />
          {translate("end_of_tenancy_cleaning")}
        </li>

        {/* Extras */}
        {showOffer && (
          <li>
            {tasks
              .valueSeq()
              .filter(
                (task: Record<Task>) =>
                  task.get("service") !==
                  END_OF_TENANCY_EXTRA_SERVICES.END_OF_TENANCY_CLEANING,
              )
              .filter((task: Record<Task>) => task.get("selected"))
              .map((task: Record<Task>, index) => {
                const testIdKey = kebabToSnake(
                  task.get("service"),
                ) as keyof typeof END_OF_TENANCY_SUMMARY_EXTRAS;
                return (
                  <TestWrapper
                    testId={END_OF_TENANCY_SUMMARY_EXTRAS[testIdKey]}
                    key={index}
                  >
                    <Extra>
                      +{" "}
                      {translate(
                        EndOfTenancyProcessHelper.getEndOfTenancyExtraServiceTranslation(
                          task.get("service"),
                        ),
                      )}
                    </Extra>
                  </TestWrapper>
                );
              })}
          </li>
        )}

        {/* Duration */}
        {showOffer && (
          <TestWrapper testId={BOOKING_SUMMARY.DURATION}>
            <li>
              <ClockTime width={theme.icons.sizes.base_x3} />
              {`${
                pricing.get("recommendedBasisHours") +
                pricing.get("recommendedExtraHours")
              }
          ${translate("sidebar_hours_of_home_cleaning")}`}
            </li>
          </TestWrapper>
        )}

        {/* Warranty */}
        {warranty && (
          <li>
            <ShieldChekmark width={theme.icons.sizes.base_x3} />
            {translate("sidebar_with_warranty")}
          </li>
        )}

        {/* Execution Date */}
        <TestWrapper testId={BOOKING_SUMMARY.DATE_AND_TIME}>
          <li>
            <CalendarScheduleLines width={theme.icons.sizes.base_x3} />
            {intlFormatDate(
              executionDateObj,
              IntlDateFormat.weekdayLongFullDate,
            )}
          </li>
        </TestWrapper>

        {/* Skip agent assignment */}
        {skipAgentAssignment && (
          <li>
            <ClockTime width={theme.icons.sizes.base_x3} />
            Skip agent assignment
          </li>
        )}
      </EndOfTenancySummaryList>

      <BoxDivider withFullWidth />

      {isSubtotalSectionVisible && (
        <StyledPriceSubpartsList>
          <React.Fragment>
            {pricing.get("discountsToDisplay").map((subpart, index) => (
              <PriceSubparts.Item
                key={`subpart-${subpart.type.toLocaleLowerCase()}-${index}`}
                testId={getPriceSubpartTestId(subpart.type)}
                variant={
                  subpart.type === TYPE_OF_SUBPARTS.VOUCHER
                    ? "voucher"
                    : "default"
                }
                price={subpart.price}
                currency={subpart.currency}
                description={subpart.description}
                tooltip={subpart.tooltip}
                onVoucherRemove={handleVoucherReset}
              >
                {subpart.label}
              </PriceSubparts.Item>
            ))}
          </React.Fragment>
        </StyledPriceSubpartsList>
      )}

      <SidebarSummaryTotal
        price={
          !showOffer
            ? pricingInitialState.displayedPriceForFirstBooking
            : pricing.get("displayedPriceForFirstBooking")
        }
        currency={pricing.get("currency")}
        tooltip={translate("price_tooltip")}
        testIds={{
          price: END_OF_TENANCY_SUMMARY.PRICE,
        }}
        translations={{
          totalText: translate("sidebar_total"),
        }}
        isBorderVisible={isSubtotalSectionVisible}
        isLoading={pricingState === LOADING_STATE.LOADING}
      />
    </StyledBox>
  );
};

export const EndOfTenancySummary = compose(withTheme)(
  EndOfTenancySummaryComponent,
);

const Extra = styled.div`
  margin-left: ${props => props.theme.margins.base_x5};
`;

const StyledBox = styled(Box)`
  ${text("regular_16")}
  padding-bottom: ${props => props.theme.margins.base_x3};
  border: 1px solid ${props => props.theme.palette.grey800};
  border-radius: 12px;

  ${props => props.theme.breakpoints.desktopLarge} {
    ${text("regular_18")}
  }
`;

const StyledPriceSubpartsList = styled(PriceSubparts.List)`
  margin-bottom: ${props => props.theme.margins.base_x1_5};

  /* Because of this @ui/Atoms/Section/Section.tsx:57 */
  > li {
    margin-bottom: 0;
  }
`;
